import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { BeatLoader } from 'react-spinners';
import 'react-datepicker/dist/react-datepicker.css';
import { dealFunderListFetchData } from '../../actions/regionActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Button, Col, Row } from 'reactstrap';

const DealFunderListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rows = useSelector((state) => state.dealFunderList);
  const isLoading = useSelector((state) => state.dealFunderListIsLoading);

  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25); // Default to 25 records

  useEffect(() => {
    dispatch(dealFunderListFetchData());
  }, [dispatch]);

  const data = useMemo(() => {
    if (!search) return rows;
    return rows.filter(
      (r) =>
        r.name?.toLowerCase().includes(search.toLowerCase()) ||
        r.regionType?.toLowerCase().includes(search.toLowerCase()) ||
        String(r.id).includes(search),
    );
  }, [rows, search]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        cell: (info) => info.getValue(),
      },
      {
        accessorKey: 'name',
        header: 'Display Name',
        cell: (info) => (
          <Link
            to={`/dealFunder/${info.row.original.id}`}
            className='row-link-active'
          >
            {info.getValue()}
          </Link>
        ),
      },
      {
        accessorKey: 'regionType',
        header: 'Region Type',
        cell: (info) => info.getValue(),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate],
  );

  const table = useReactTable({
    data,
    columns,
    state: { pagination: { pageIndex, pageSize } },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: false,
    pageCount: Math.ceil(data.length / pageSize),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1; // 1-based

  const totalButtons = 5; // Number of page buttons to show
  let pageButtons = [];

  if (pageCount <= totalButtons) {
    pageButtons = [...Array(pageCount)].map((_, i) => i + 1);
  } else if (currentPage <= Math.ceil(totalButtons / 2)) {
    // Show first 5 pages
    pageButtons = [...Array(totalButtons)].map((_, i) => i + 1);
  } else if (currentPage >= pageCount - Math.floor(totalButtons / 2)) {
    // Show last 5 pages
    pageButtons = [...Array(totalButtons)].map(
      (_, i) => pageCount - totalButtons + 1 + i,
    );
  } else {
    // Center current page
    const start = currentPage - Math.floor(totalButtons / 2);
    pageButtons = [...Array(totalButtons)].map((_, i) => start + i);
  }

  return (
    <div className='deal-funder'>
      <h4>Manage Deal Funders</h4>
      <Row className='mb-2 d-flex justify-content-between align-items-center'>
        <Col>
          <Link to='/dealFunder/new' className='btn btn-success mb-2'>
            <FontAwesomeIcon icon={faPlus} /> Add
          </Link>
        </Col>
        <Col xs={6} sm={6} md={6} lg={4}>
          <input
            className='form-control'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search funders...'
            name='search'
          />
        </Col>
      </Row>
      <table className='table table-bordered table-blue'>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, idx) => (
                <th
                  key={header.id}
                  className='text-dark fw-bold align-middle column-header'
                  style={{
                    width:
                      idx === 0
                        ? '250px'
                        : idx === 1
                          ? '100%'
                          : idx === 2
                            ? '150px'
                            : undefined,
                    minWidth:
                      idx === 0
                        ? '250px'
                        : idx === 1
                          ? '100px'
                          : idx === 2
                            ? '150px'
                            : undefined,
                    maxWidth:
                      idx === 0 ? '250px' : idx === 2 ? '150px' : undefined,
                  }}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {isLoading ? (
            <tr className='campaign-row'>
              <td colSpan={columns.length}>
                <BeatLoader color={'#004C5F'} />
              </td>
            </tr>
          ) : table.getRowModel().rows.length === 0 ? (
            <tr className='campaign-row'>
              <td colSpan={columns.length}>No records</td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id} className='campaign-row'>
                {row.getVisibleCells().map((cell, idx) => (
                  <td
                    className='campaign-row'
                    key={cell.id}
                    style={{
                      width:
                        idx === 0
                          ? '250px'
                          : idx === 1
                            ? '100%'
                            : idx === 2
                              ? '150px'
                              : undefined,
                      minWidth:
                        idx === 0
                          ? '250px'
                          : idx === 1
                            ? '100px'
                            : idx === 2
                              ? '150px'
                              : undefined,
                      maxWidth:
                        idx === 0 ? '250px' : idx === 2 ? '150px' : undefined,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {!isLoading && table.getRowModel().rows.length > 0 && (
        <nav aria-label='Page navigation' className='mt-3'>
          <div className='d-flex align-items-center justify-content-between'>
            {/* Page size dropdown on the left */}
            <div>
              <select
                className='btn btn-default btn-secondary dropdown-toggle'
                value={pageSize}
                name='selectPageSize'
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPageIndex(0); // Reset to first page when page size changes
                }}
              >
                {[10, 25, 30, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            {/* Pagination controls on the right */}
            <ul className='pagination mb-0'>
              {/* First and Previous */}
              {currentPage > 3 && (
                <li>
                  <button
                    className='page-link'
                    onClick={() => setPageIndex(0)}
                    disabled={currentPage === 1}
                  >
                    {'<<'}
                  </button>
                </li>
              )}
              {currentPage > 1 && (
                <li>
                  <button
                    className='page-link'
                    onClick={() => setPageIndex(currentPage - 2)}
                    disabled={currentPage === 1}
                  >
                    {'<'}
                  </button>
                </li>
              )}
              {/* Page numbers */}
              {pageButtons.map((page) => (
                <li
                  key={page}
                  className={`page-item${currentPage === page ? ' active' : ''}`}
                >
                  <button
                    className='page-link'
                    onClick={() => setPageIndex(page - 1)}
                  >
                    {page}
                  </button>
                </li>
              ))}
              {/* Next and Last */}
              {currentPage < pageCount && (
                <li>
                  <button
                    className='page-link'
                    onClick={() => setPageIndex(currentPage)}
                    disabled={currentPage === pageCount}
                  >
                    {'>'}
                  </button>
                </li>
              )}
              {currentPage < pageCount - 2 && (
                <li>
                  <button
                    className='page-link'
                    onClick={() => setPageIndex(pageCount - 1)}
                    disabled={currentPage === pageCount}
                  >
                    {'>>'}
                  </button>
                </li>
              )}
            </ul>
          </div>
        </nav>
      )}
    </div>
  );
};

export default DealFunderListPage;
